<template>
    <div class="height100 scroll-y">
        <div v-if="isLoading">
            <ProgressCircular class="pt-10" :color="currency.unit" />
        </div>

        <v-container v-if="!isLoading" fluid class="ma-0 pa-0">
            <LimitAbleLayout
                :init-values="filterInitValues"
                clear-dates-on-all
                @reload-data="reloadData">
                <template slot="title">
                    <h1>Timelines Graphs</h1>
                </template>
                <template slot="body" class="white">
                    <div>
                        <v-container fluid class="pa-0 ma-0">
                            <ProgressCircular v-if="balanceLoading" class="pt-10" :color="currency.unit"/>
                            <div v-else-if="!balanceLoading && balance.length === 0">
                                <div class="d-flex align-center justify-center pt-10" style="height: 80%; color: rgba(0,0,0,0.6)">
                                    <div class="title">No data matched provided filter!</div>
                                </div>
                            </div>
                            <HighCharts v-else style="user-select: none" :options="chartOptions"/>
                        </v-container>
                    </div>
                </template>
            </LimitAbleLayout>
        </v-container>
    </div>
</template>

<script>
import {Chart} from 'highcharts-vue'
import ProgressCircular from "@/components/common/ProgressCircular"
import Highcharts from 'highcharts'
import exportingInit from 'highcharts/modules/exporting'
import offlineExportingInit from 'highcharts/modules/offline-exporting'
import LimitAbleLayout from "@/components/common/LimitAbleLayout"
import {formatDateTime} from "@/utils/filters"
import {mapGetters} from "vuex";

Highcharts.dateFormats = {
    'common': function (time) {
        return formatDateTime(time)
    }
}

export default {
    name: 'ClusterChartBalance',
    components: {
        ProgressCircular,
        HighCharts: Chart,
        LimitAbleLayout,
    },
    beforeUpdate() {
        this.$emit("component-loaded-event");
    },
    created() {
        Highcharts.setOptions({
            lang: {
                thousandsSep: ','
            }
        })
    },
    computed: {
        ...mapGetters(["currentFiat"]),
        filterInitValues() {
            const clusterDetails = this.$store.getters.clusterDetails;
            if (clusterDetails && clusterDetails.firstDepositBlocktime) {
                return {
                    since: clusterDetails.firstDepositBlocktime,
                    until: clusterDetails.lastDepositBlocktime || Math.floor(Date.now() / 1000),
                    selectedButtonIndex: 1,
                    offset: {
                        amount: 1,
                        unit: 'week'
                    }
                }
            }
            return {
                since: 1,
                until: Math.floor(Date.now() / 1000),
                selectedButtonIndex: 1,
                offset: {
                    amount: 1,
                    unit: 'week'
                }
            }
        },
        currency() {
            return this.$store.getters.currency
        },
        balance() {
            return this.$store.getters.clusterBalance
        },
        fiats() {
            return this.$store.getters.clusterTimelineFiats
        },
        altBalance() {
            if (this.balance.length > 0 && this.fiats.length > 0) {
                const currentFiatUnit = this.currentFiat.unit
                if (this.altBalanceCache.has(`${this.currentLimitValue}${currentFiatUnit}`) && !this.filterChanged) {
                    this.filterChanged = false
                    return this.altBalanceCache.get(`${this.currentLimitValue}${currentFiatUnit}`)
                }

                let arrayOfTimestampBalanceTuples = this.fiats.map((fiat, index) => {
                    const fiatConversionRate = fiat[1][currentFiatUnit]
                    const timestamp = fiat[0]
                    const altBalance = (this.balance[index][1] * fiatConversionRate)

                    if (fiatConversionRate) {
                        return [timestamp, altBalance]
                    }
                })

                if (arrayOfTimestampBalanceTuples.some(e => e === undefined)) {
                    this.$store.dispatch('warning', 'Missing conversion rates for the selected currency.')
                }

                arrayOfTimestampBalanceTuples = arrayOfTimestampBalanceTuples.filter(e => e)

                this.altBalanceCache.set(`${this.currentLimitValue}${currentFiatUnit}`, arrayOfTimestampBalanceTuples)
                return arrayOfTimestampBalanceTuples
            }
        },
        clusterEntityId() {
            return this.$route.params.clusterentity
        },
        clusterInfo() {
            return this.$store.getters.clusterInfo
        },
        isLoading() {
            return this.$store.getters.clusterInfoLoading
        },
        balanceLoading() {
            return this.$store.getters.clusterBalanceLoading
        },
        volume() {
            return this.$store.getters.clusterVolume
        },
        altBalanceHasData() {
            return this.altBalance?.length > 0
        },
        chartSeriesArray() {
            const finalSeriesArray = [
                {
                    type: 'line',
                    lineWidth: 2,
                    color: this.$vuetify.theme.currentTheme[this.currency.unit],
                    name: this.currency.unit + ' balance',
                    data: this.balance,
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:,.f}</b><br/>',
                    }
                },
                {
                    type: 'column',
                    color: '#84bcff',
                    yAxis: 2,
                    name: 'Transactions',
                    data: this.volume,
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:,.0f}</b><br/>'
                    },
                }
            ]

            if (this.altBalanceHasData) {
                finalSeriesArray.push({
                        type: 'line',
                        lineWidth: 2,
                        color: '#555555',
                        dashStyle: 'shortdot',
                        yAxis: 1,
                        name: `${this.currentFiat.unit} balance`,
                        data: this.altBalance,
                        tooltip: {
                            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:,.2f}</b><br/>',
                        }
                    })
            }

            return finalSeriesArray
        },
        chartOptions() {
            return {
                chart: {
                    zoomType: 'x',
                    height: 800,
                },

                credits: {
                    enabled: false
                },

                title: {
                    text: this.currency === false ? '' : `${this.currency.unit} cluster <span class="fontMonospace">${this.clusterEntityId}</span>`
                },
                subtitle: {
                    text: document.ontouchstart === undefined ? 'Click and drag in the plot area to zoom in' : 'Pinch the chart to zoom in'
                },
                xAxis: [
                    {
                        type: 'datetime',
                        minRange: 3 * 1000,
                        height: '20%',
                        top: '80%',
                        lineWidth: 2,
                        lineColor: "#42424288"
                    },
                    {
                        minRange: 3 * 1000,
                        height: '54.6%',
                        // Zero / pane-separator line is drawn as a yAxis[0] plotLine at value 0
                        // (see below) so it stays on the 0 gridline in every render (live AND
                        // PNG export). The x-axis line itself is auto-positioned and drifts off
                        // zero when the export re-renders at a different size, so keep it hidden.
                        lineWidth: 0,
                        lineColor: "#42424288"
                    },
                ],
                yAxis: [
                    {
                        title: {
                            text: this.currency === false ? '' : 'Balance (' + this.currency.unit + ')'
                        },
                        min: 0,
                        floor: 0,
                        height: '60%',
                        // Bold zero line / separator between the balance and activity panes.
                        // Anchored to value 0 on the balance axis, so it always sits exactly on
                        // the 0 gridline regardless of render size (fixes the PNG-export offset).
                        plotLines: [{
                            value: 0,
                            width: 2,
                            color: '#42424288',
                            zIndex: 5
                        }]
                    },
                    {
                        title: {
                            text: 'Balance (' + this.currentFiat.unit + ')'
                        },
                        opposite: true,
                        min: 0,
                        floor: 0,
                        height: '60%',
                    },
                    {
                        title: {
                            text: 'Transactions'
                        },
                        offset: 0,
                        min: 0,
                        floor: 0,
                        tickInterval: 1,
                        height: '20%',
                        top: '80%',
                    },
                ],
                tooltip: {
                    shared: true,
                    pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.8f}</b><br/>',
                    xDateFormat: "%common"
                },
                scrollbar: {
                    liveRedraw: false
                },
                legend: {
                    enabled: true
                },
                plotOptions: {
                    line: {
                        step: "left",
                        fillColor: {
                            linearGradient: {
                                x1: 0,
                                y1: 0,
                                x2: 0,
                                y2: 1
                            },
                            stops: [
                                [0, this.$vuetify.theme.currentTheme[this.currency.unit]],
                                [1, 'rgba(255,255,255,0)']
                            ]
                        },
                    },
                    series: {
                        states: {
                            inactive: {
                                opacity: 1
                            }
                        }
                    }
                },
                series: this.chartSeriesArray,
                exporting: {
                    // Render exports in the browser. fallbackToExportServer:false guarantees
                    // chart data is NEVER POSTed to export.highcharts.com (third-party).
                    fallbackToExportServer: false,
                    sourceWidth: 1920,
                    sourceHeight: 1080,
                    chartOptions: {
                        subtitle: null
                    },
                    buttons: {
                        contextButton: {
                            // PNG/SVG download + Print (use "Save as PDF" in the print dialog),
                            // all client-side. Direct downloadPDF needs jsPDF (see note).
                            menuItems: ['downloadPNG', 'downloadSVG', 'printChart'],
                        },
                    },
                }
            }
        }
    },
    beforeCreate() {
        exportingInit(Highcharts);
        offlineExportingInit(Highcharts);
    },
    methods: {
        reloadData(data) {
            this.$store.dispatch('loadClusterBalance', { ...data })
            this.currentLimitValue = data
            this.filterChanged = true
        }
    },
    data() {
        return {
            currentLimitValue: undefined,
            altBalanceCache: new Map(),
            filterChanged: true
        }
    }
}
</script>

<style scoped>
</style>
